import { useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ReactComponent as DropdownSvg } from "../../asserts/logo/dropdown.svg";
import { ReactComponent as WarningTriangleIcon } from "../../asserts/logo/WarningTraingleIcon.svg";
import { pingMonitoringUrl } from "../../services/monitoringReachabilityClient";
import { validateMonitoringSignalsUrl } from "../../utils/validateApiBaseUrl";

const FONT = "Lato, sans-serif";

/** Same rule as the Entry Check plan link: https only, with a real host. */
const LINK_FIELD_IDS = new Set(["monitors", "sampling", "trace"]);

function isValidHttpsLink(value) {
  const link = String(value || "").trim();
  if (!/^https:\/\//i.test(link) || /\s/.test(link)) return false;
  try {
    const url = new URL(link);
    const host = url.hostname;
    if (url.protocol !== "https:" || !host) return false;
    if (host.toLowerCase() === "localhost") return true;
    return host.includes(".") && !host.startsWith(".") && !host.endsWith(".");
  } catch {
    return false;
  }
}

const MD_FIELD_IDS = new Set(["incident", "rollback"]);
const MD_MAX_BYTES = 1024 * 1024;
const MD_MIN_CHARS = 50;
const MD_REQUIRED_MSG = "This field is required.";
const MD_TYPE_MSG = "Only .md files are allowed.";
const MD_SIZE_MSG = "File size must be less than 1 MB.";
const MD_EMPTY_MSG = "File appears to be empty. Please add content.";
const MD_SHORT_MSG = "File must contain at least 50 characters.";
const MD_SUCCESS_MSG = "File validated successfully.";

function fieldSatisfied(item, value, mdFiles) {
  if (MD_FIELD_IDS.has(item.id)) {
    const file = mdFiles?.[item.id];
    return Boolean(file?.name && !file.error);
  }
  const trimmed = String(value || "").trim();
  if (item.id === "monitors") {
    if (!trimmed) return !item.required;
    return validateMonitoringSignalsUrl(trimmed).ok;
  }
  if (LINK_FIELD_IDS.has(item.id)) {
    if (!trimmed) return !item.required;
    return isValidHttpsLink(trimmed);
  }
  if (!item.required) return true;
  return trimmed !== "";
}

function readFileText(file) {
  if (typeof file?.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

async function inspectMdFile(file) {
  const name = file?.name || "";
  if (!name.toLowerCase().endsWith(".md")) return { name, error: MD_TYPE_MSG };
  if (file.size > MD_MAX_BYTES) return { name, error: MD_SIZE_MSG };
  let text = "";
  try {
    text = await readFileText(file);
  } catch {
    return { name, error: MD_EMPTY_MSG };
  }
  const content = text.trim();
  if (!content) return { name, error: MD_EMPTY_MSG };
  if (content.length < MD_MIN_CHARS) return { name, error: MD_SHORT_MSG };
  return { name, error: "", content: text };
}

const ITEMS = [
  {
    id: "monitors",
    tone: "hard",
    required: true,
    label: "Monitoring dashboards/monitors linked",
    kind: "url",
    placeholder: "https://evidence.banking.validAIte.io/gate3/retail-assistant-readiness-1",
    action: "Ping to confirm reachable",
    help: "Tracks live trust signal (refusal rate, latency) — we verify the dashboard responds, not its configuration.",
    tipTitle: "What it is:",
    tipBody:
      "Monitoring dashboards track this system's trust signals — things like refusal rate, latency, or flagged-output volume — once it's live in production, so problems are visible as they happen rather than discovered after the fact.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite performs a simple reachability check only. It does not verify the dashboard's configuration, what it measures, or whether its alerts are properly tuned — that remains a separate operational responsibility.",
  },
  {
    id: "thresholds",
    tone: "soft",
    label: "Alert thresholds defined",
    kind: "text",
    defaultValue: "alert-rules-rba.yaml",
    help: "Defines when a signal is unacceptable and who gets notified — we confirm it exists, not that thresholds are correct.",
    tipTitle: "What it is:",
    tipBody:
      "Alert thresholds define the point at which a monitored trust signal is considered unacceptable, so the right people are notified automatically the moment it's crossed — instead of relying on someone to notice by watching a dashboard.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite is confirming this was thought about and documented, not re-performing the underlying operational review itself.",
  },
  {
    id: "incident",
    tone: "hard",
    required: true,
    label: "Incident workflow defined",
    kind: "md",
    placeholder: "Choose a file (.md)",
    help: "Defines triage severity and on-call routing — we confirm it's documented, not that the workflow works.",
    tipTitle: "What it is:",
    tipBody:
      "The incident workflow defines how an incident gets triaged (its severity classification) and who gets paged to respond (on-call routing), so the response doesn't have to be improvised in the moment something goes wrong.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite is confirming this was thought about and documented, not re-performing the underlying operational review itself.",
  },
  {
    id: "rollback",
    tone: "hard",
    required: true,
    label: "Rollback/kill-switch plan defined",
    kind: "md",
    placeholder: "Choose a file (.md)",
    help: "How the system gets reversed if it needs to stop — we confirm a plan exists, not that it executes.",
    tipTitle: "What it is:",
    tipBody:
      "A rollback / kill-switch plan is how the system gets turned off or reverted quickly if it needs to stop operating. This matters for every system, but especially for agentic ones that take real actions on their own.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite is confirming this was thought about and documented, not re-performing the underlying operational review itself.",
  },
  {
    id: "sampling",
    tone: "soft",
    label: "Post-launch sampling plan defined",
    kind: "text",
    placeholder: "supporting evidence link...",
    help: "How live outputs keep getting reviewed post-launch — we confirm a plan exists, not its methodology.",
    tipTitle: "What it is:",
    tipBody:
      "A post-launch sampling plan is how live outputs keep getting reviewed after go-live, since a system's behavior in production can diverge from what was seen during testing.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite is confirming this was thought about and documented, not re-performing the underlying operational review itself.",
  },
  {
    id: "cadence",
    tone: "soft",
    label: "Trust review cadence set",
    kind: "select",
    placeholder: "Select a cadence",
    options: ["Weekly", "Bi-Weekly", "Monthly", "Custom recurring day"],
    help: "How often the system gets reassessed after launch — we capture the schedule, not trigger reviews.",
    tipTitle: "What it is:",
    tipBody:
      "This sets how often the system's full posture gets reassessed after launch — a guaranteed minimum check-in, distinct from reacting to a change the moment it happens.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "Gate 3 only captures this as a baseline schedule value for a future Gate 4 to read. It does not implement, trigger, or simulate any recurring review — reacting immediately when something changes is Gate 4's job, out of scope for this release.",
  },
  {
    id: "trace",
    tone: "hard",
    required: true,
    label: "Action-trace capture (agentic systems only)",
    kind: "url",
    placeholder: "https://evidence.banking.validAIte.io/gate3/retail-assistant-readiness-7",
    help: "Records every action for reconstruction and audit — we confirm logging exists, not its completeness.",
    tipTitle: "What it is:",
    tipBody:
      "Action-trace capture is a record of every action this system actually took in production, so its behavior can be reconstructed and audited afterward — something only relevant for a system that autonomously takes actions.",
    scopeTitle: "Scope boundary:",
    scopeBody:
      "ValidAite is confirming this was thought about and documented, not re-performing the underlying operational review itself.",
  },
];

const PILL_BASE = {
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "100%",
  height: { xs: 22, sm: 22, lg: 24 },
  borderRadius: "12px",
  letterSpacing: 0,
  textAlign: "center",
  whiteSpace: "nowrap",
};

const PILL = {
  hard: {
    ...PILL_BASE,
    width: { xs: "auto", sm: "auto", lg: 114 },
    px: { xs: "10px", sm: "12px", lg: "16px" },
    py: 0,
    gap: { xs: "6px", lg: "10px" },
    border: "1px solid #DE350B",
    bgcolor: "#FFEFEB",
    color: "#B82500",
    fontWeight: 600,
    fontSize: { xs: 11, sm: 11, lg: 12 },
    lineHeight: { xs: "22px", sm: "22px", lg: "24px" },
  },
  soft: {
    ...PILL_BASE,
    width: { xs: "auto", sm: "auto", lg: 141 },
    px: { xs: "8px", sm: "10px", lg: "12px" },
    py: { xs: "2px", sm: "2px", lg: "4px" },
    border: "1px solid #9551EC",
    bgcolor: "#F5EEFF",
    color: "#9551EC",
    fontWeight: 700,
    fontSize: { xs: 11, sm: 11, lg: 12 },
    lineHeight: { xs: "16px", sm: "16px", lg: 1 },
  },
};

const TIP_TYPE = { fontSize: { xs: 12, sm: 12, lg: 13 }, lineHeight: { xs: "16px", sm: "17px", lg: "18px" } };

function InfoTip({ item }) {
  const title = (
    <Box sx={{ p: 0.5, maxWidth: { xs: 220, sm: 260, lg: 280 } }}>
      {item.tipTitle ? (
        <>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, ...TIP_TYPE, color: "#FFFFFF" }}>
            {item.tipTitle}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, ...TIP_TYPE, color: "#FFFFFF", mt: 0.5 }}>
            {item.tipBody}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, ...TIP_TYPE, color: "#FFFFFF", mt: 1 }}>
            {item.scopeTitle}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, ...TIP_TYPE, color: "#FFFFFF", mt: 0.5 }}>
            {item.scopeBody}
          </Typography>
        </>
      ) : (
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, ...TIP_TYPE, color: "#FFFFFF" }}>
          {item.help}
        </Typography>
      )}
    </Box>
  );
  return (
    <Tooltip
      title={title}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#000000",
            borderRadius: "4px",
            p: { xs: 1, lg: 1.5 },
            maxWidth: { xs: 240, sm: 280, lg: 320 },
          },
        },
      }}
    >
      <Box component="button" type="button" aria-label={`About ${item.label}`} sx={{ border: "none", bgcolor: "transparent", p: 0, display: "inline-flex", cursor: "pointer", color: "#000000", flexShrink: 0 }}>
        <InfoOutlinedIcon sx={{ fontSize: { xs: 14, sm: 15, lg: 16 }, color: "#000000" }} />
      </Box>
    </Tooltip>
  );
}

function ChecklistSelectIcon({ className, style }) {
  return (
    <Box
      component="span"
      className={className}
      style={style}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: { xs: 18, sm: 20, lg: 24 },
        height: { xs: 18, sm: 20, lg: 24 },
        "& svg": { width: "100%", height: "100%", display: "block" },
      }}
    >
      <DropdownSvg aria-hidden focusable="false" />
    </Box>
  );
}

const FIELD_SX = {
  flex: 1,
  minWidth: 0,
  width: "100%",
  height: { xs: 40, sm: 36, lg: 32 },
  boxSizing: "border-box",
  border: "1px solid #707070",
  borderRadius: "4px",
  px: { xs: "8px", sm: "10px", lg: "10px" },
  py: { xs: "8px", sm: "6px", lg: "4px" },
  bgcolor: "#FFFFFF",
  boxShadow: "inset 0px 2px 3px 1px #55555533",
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: { xs: 14, sm: 15, lg: 16 },
  lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
  letterSpacing: 0,
  color: "#000000",
  outline: "none",
  "&::placeholder": { color: "#8D8D8D", opacity: 1, fontSize: "inherit" },
  "&:focus": { borderColor: "#707070" },
};

const SELECT_SX = {
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    height: { xs: 40, sm: 36, lg: 32 },
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: { xs: 14, sm: 15, lg: 16 },
    lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
    letterSpacing: 0,
    color: "#000000",
    bgcolor: "#FFFFFF",
    borderRadius: "4px",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: "unset !important",
    height: "auto",
    boxSizing: "border-box",
    py: { xs: "8px", sm: "6px", lg: "4px" },
    pl: { xs: "8px", lg: "10px" },
    pr: { xs: "28px", sm: "32px", lg: "36px" },
  },
  "& .MuiSelect-icon": {
    right: { xs: 6, lg: 8 },
    top: "50%",
    transform: "translateY(-50%)",
  },
  "& .MuiSelect-iconOpen": {
    transform: "translateY(-50%) rotate(180deg)",
  },
};

/** Saved determination base codes for Agentic systems (A9) and multi-agent systems (A12). */
export function isSupportedAgenticArchetype(baseCode) {
  const code = String(baseCode || "").trim().toUpperCase();
  return code === "A9" || code === "A12";
}

const TRACE_WARNING = "Action-Trace Capture is available only for Agentic AI systems.";

function MdUploadField({ item, file, onPick, onClear }) {
  const inputRef = useRef(null);
  const hasFile = Boolean(file?.name);
  const invalid = hasFile && Boolean(file.error);
  const valid = hasFile && !file.error;
  const showRequired = !hasFile && file?.error === MD_REQUIRED_MSG;
  const border = invalid ? "1px solid #F04438" : valid ? "1px solid #12B76A" : "1px solid #D0D5DD";
  const bgcolor = invalid ? "#FFFBFA" : valid ? "#F6FEF9" : "#FFFFFF";
  const iconColor = invalid ? "#F04438" : valid ? "#027A48" : "#98A2B3";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: 0, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxSizing: "border-box",
          width: "100%",
          minHeight: { xs: 40, sm: 36, lg: 36 },
          border,
          borderRadius: "8px",
          bgcolor,
          px: "10px",
          py: "4px",
        }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: iconColor, flexShrink: 0 }} />
        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: FONT,
            fontWeight: 400,
            fontSize: { xs: 13, lg: 14 },
            lineHeight: "20px",
            color: hasFile ? "#344054" : "#98A2B3",
          }}
        >
          {hasFile ? file.name : item.placeholder}
        </Typography>
        {hasFile ? (
          <IconButton
            size="small"
            aria-label={`Remove ${item.label} file`}
            onClick={() => {
              if (inputRef.current) inputRef.current.value = "";
              onClear();
            }}
            sx={{ p: "2px", color: "#667085" }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            disableElevation
            onClick={() => inputRef.current?.click()}
            sx={{
              textTransform: "none",
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: 13,
              lineHeight: "18px",
              color: "#344054",
              border: "1px solid #D0D5DD",
              borderRadius: "6px",
              bgcolor: "#FFFFFF",
              minWidth: 72,
              height: 28,
              px: "10px",
              flexShrink: 0,
              "&:hover": { border: "1px solid #D0D5DD", bgcolor: "#F9FAFB" },
            }}
          >
            Browse
          </Button>
        )}
        <Box
          component="input"
          ref={inputRef}
          type="file"
          accept=".md,text/markdown"
          aria-label={item.label}
          onChange={(e) => {
            const next = e.target.files?.[0];
            e.target.value = "";
            if (next) onPick(next);
          }}
          sx={{ display: "none" }}
        />
      </Box>
      {invalid || showRequired ? (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
          <ErrorOutlineIcon sx={{ fontSize: 16, color: "#D92D20", mt: "1px", flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", color: "#D92D20" }}>
            {file.error}
          </Typography>
        </Box>
      ) : null}
      {valid ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#027A48", flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: 12, lineHeight: "18px", color: "#027A48" }}>
            {MD_SUCCESS_MSG}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function ChecklistCard({
  item,
  value,
  onChange,
  onPing,
  pinging = false,
  pingResult = null,
  customDays,
  onCustomDays,
  disabled = false,
  warning = "",
  mdFile,
  onMdPick,
  onMdClear,
}) {
  const pill = PILL[item.tone];
  const httpsOnlyLink = item.id === "sampling" || item.id === "trace";
  const linkInvalid = httpsOnlyLink && String(value || "").trim() !== "" && !isValidHttpsLink(value);
  return (
    <Box
      sx={{
        border: "1px solid #E6E6E6",
        borderRadius: { xs: "6px", lg: "8px" },
        bgcolor: "#FFFFFF",
        p: { xs: 1.25, sm: 1.5, lg: 2 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0.75, sm: 1, lg: 1.25 },
        borderRadius: { xs: "6px", lg: "8px" },
      }}
    >
      <Box
        component="span"
        sx={{
          alignSelf: "flex-start",
          borderRadius: "12px",
          fontFamily: FONT,
          ...pill,
        }}
      >
        {item.tone === "hard" ? "Hard Condition" : "Soft gap if incomplete"}
      </Box>
      <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: 13, sm: 13, lg: 14 }, lineHeight: { xs: "18px", sm: "20px", lg: "21px" }, letterSpacing: 0, color: "#707070" }}>
        {item.required && !disabled ? (
          <Box component="span" sx={{ color: "#D92D20", mr: 0.25 }} aria-hidden>
            *
          </Box>
        ) : null}
        {item.label}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: "10px", sm: "8px", lg: "8px" }, alignItems: { xs: "stretch", sm: "center" } }}>
        {item.kind === "md" ? (
          <MdUploadField item={item} file={mdFile} onPick={onMdPick} onClear={onMdClear} />
        ) : item.kind === "select" ? (
          <TextField
            select
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            SelectProps={{
              displayEmpty: true,
              IconComponent: ChecklistSelectIcon,
              renderValue: (selected) => selected || item.placeholder,
            }}
            sx={{
              ...SELECT_SX,
              "& .MuiSelect-select": {
                ...SELECT_SX["& .MuiSelect-select"],
                color: value ? "#000000" : "#8D8D8D",
              },
            }}
          >
            {item.options.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: "20px", sm: "22px", lg: "24px" }, color: "#000000" }}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Box
            component="input"
            aria-label={item.label}
            value={value}
            placeholder={item.placeholder || ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            sx={{
              ...FIELD_SX,
              ...(disabled
                ? {
                    bgcolor: "#F5F5F5",
                    color: "#8D8D8D",
                    cursor: "not-allowed",
                    "&:disabled": {
                      bgcolor: "#F5F5F5",
                      color: "#8D8D8D",
                      WebkitTextFillColor: "#8D8D8D",
                    },
                  }
                : null),
              ...(linkInvalid ? { border: "1px solid #D92D20" } : null),
            }}
          />
        )}
        {item.action ? (
          <Button
            variant="outlined"
            disableElevation
            type="button"
            disabled={pinging || (httpsOnlyLink && !isValidHttpsLink(value))}
            aria-busy={pinging || undefined}
            onClick={onPing}
            sx={{
              textTransform: "none",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: 12, sm: 12, lg: 13 },
              lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
              letterSpacing: 0,
              color: "#555555",
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              width: { xs: "100%", sm: "auto", lg: 180 },
              minWidth: { xs: 0, lg: 180 },
              height: { xs: 40, sm: 36, lg: 32 },
              minHeight: { xs: 40, sm: 36, lg: 32 },
              px: { xs: "12px", lg: "16px" },
              py: { xs: "6px", sm: "6px", lg: "4px" },
              borderRadius: "4px",
              border: "1px solid #BBBBBB",
              bgcolor: "#F5F5F5",
              backgroundImage:
                "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
              boxShadow: "0px 1px 2px 0px #00000014",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "& .MuiTouchRipple-root": { display: "none" },
              "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
                border: "1px solid #BBBBBB",
                color: "#555555",
                bgcolor: "#F5F5F5",
                backgroundColor: "#F5F5F5",
                backgroundImage:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
                boxShadow: "0px 1px 2px 0px #00000014",
              },
              "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
                outline: "none",
                color: "#555555",
                bgcolor: "#F5F5F5 !important",
                backgroundColor: "#F5F5F5 !important",
                backgroundImage:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5) !important",
                border: "2px solid #555555",
                boxShadow: "0px 1px 2px 0px #00000014",
              },
              "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
                color: "#555555",
                bgcolor: "#F5F5F5 !important",
                backgroundColor: "#F5F5F5 !important",
                border: "2px solid #555555",
              },
              "&:active:not(.Mui-disabled)": {
                color: "#555555",
                bgcolor: "#F5F5F5 !important",
                backgroundColor: "#F5F5F5 !important",
                backgroundImage:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5) !important",
                border: "1px solid #BBBBBB",
                boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-disabled": {
                color: "#BBBBBB !important",
                bgcolor: "#F5F5F5 !important",
                backgroundColor: "#F5F5F5 !important",
                backgroundImage: "none !important",
                border: "1px solid #E6E6E6 !important",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
                opacity: "1 !important",
                cursor: "not-allowed",
              },
            }}
          >
            {pinging ? "Checking…" : item.action}
          </Button>
        ) : null}
      </Box>
      {pingResult?.message ? (
        <Typography
          role="status"
          sx={{
            fontFamily: FONT,
            fontWeight: 400,
            fontSize: { xs: 12, lg: 13 },
            lineHeight: "18px",
            letterSpacing: 0,
            color: pingResult.ok ? "#00754E" : "#B82500",
          }}
        >
          {pingResult.message}
        </Typography>
      ) : null}
      {warning ? (
        <Box
          role="status"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            boxSizing: "border-box",
            px: "8px",
            py: "8px",
            borderRadius: "8px",
            bgcolor: "#FFF8E0",
          }}
        >
          <Box sx={{ width: 16, height: 16, flexShrink: 0, display: "flex", "& svg": { width: "100%", height: "100%", display: "block" } }}>
            <WarningTriangleIcon />
          </Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 12, lg: 13 }, lineHeight: "18px", letterSpacing: 0, color: "#8A5C00" }}>
            {warning}
          </Typography>
        </Box>
      ) : null}
      {linkInvalid ? (
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 11, lg: 12 }, lineHeight: 1.3, color: "#D92D20" }}>
          Enter a valid link that starts with https
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", lg: "center" }, gap: { xs: "6px", lg: "4px" } }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 11, sm: 11, lg: 11 }, lineHeight: { xs: 1.4, sm: 1.35, lg: 1 }, letterSpacing: 0, color: "#8D8D8D" }}>
          {item.help}
        </Typography>
        <InfoTip item={item} />
      </Box>
      {item.id === "cadence" && value === "Custom recurring day" ? (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: { xs: "6px", lg: "8px" } }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: "20px", sm: "22px", lg: "24px" }, letterSpacing: 0, color: "#000000" }}>
            Every
          </Typography>
          <Box
            component="input"
            type="text"
            inputMode="numeric"
            aria-label="Custom cadence in days"
            value={customDays}
            onChange={(e) => onCustomDays(e.target.value.replace(/\D/g, "").slice(0, 3).replace(/^0+/, ""))}
            sx={{
              ...FIELD_SX,
              flex: "0 0 auto",
              width: { xs: 52, sm: 56, lg: 64 },
              textAlign: "left",
              pl: { xs: "8px", lg: "10px" },
              pr: "8px",
            }}
          />
          <Typography sx={{ fontFamily: FONT, fontWeight: 400, fontSize: { xs: 14, sm: 15, lg: 16 }, lineHeight: { xs: "20px", sm: "22px", lg: "24px" }, letterSpacing: 0, color: "#000000" }}>
            days
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

export function initialChecklistValues() {
  return Object.fromEntries(ITEMS.map((item) => [item.id, item.defaultValue || ""]));
}

/** Labels of incomplete soft-gap checklist items (conditional gaps). */
export function getSoftConditionalGaps(values, customDays = "0") {
  const isComplete = (item, value) => {
    const trimmed = String(value ?? "").trim();
    if (item.kind === "select") {
      if (!trimmed) return false;
      if (trimmed === "Custom recurring day") {
        const days = Number(String(customDays ?? "").trim());
        return Number.isFinite(days) && days > 0;
      }
      return true;
    }
    return trimmed !== "";
  };

  return ITEMS.filter((item) => item.tone === "soft" && !isComplete(item, values?.[item.id])).map((item) => item.label);
}

export default function Gate3ReadinessChecklist({
  onReady,
  values,
  onValuesChange,
  customDays = "0",
  onCustomDays,
  mdFiles = { incident: null, rollback: null },
  onMdFilesChange,
  agenticTraceEnabled = false,
}) {
  const [ping, setPing] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [mdErrors, setMdErrors] = useState({ incident: null, rollback: null });
  const valuesRef = useRef(values);
  const filesRef = useRef(mdFiles);
  valuesRef.current = values;
  filesRef.current = mdFiles;
  const shownFiles = {
    incident: mdErrors.incident || (mdFiles?.incident?.name ? { name: mdFiles.incident.name, error: "" } : null),
    rollback: mdErrors.rollback || (mdFiles?.rollback?.name ? { name: mdFiles.rollback.name, error: "" } : null),
  };
  const traceDisabled = agenticTraceEnabled !== true;
  const requiredFilled = ITEMS.every((item) => {
    if (item.id === "trace" && traceDisabled) return true;
    return fieldSatisfied(item, values?.[item.id], shownFiles);
  });

  const setMdResult = (id, result) => {
    const saved = result?.name && !result.error ? { name: result.name, content: result.content || "" } : null;
    const nextFiles = { ...filesRef.current, [id]: saved };
    filesRef.current = nextFiles;
    onMdFilesChange?.(nextFiles);
    setMdErrors((prev) => ({ ...prev, [id]: saved ? null : { name: result?.name || "", error: result?.error || MD_REQUIRED_MSG } }));
    const next = { ...valuesRef.current, [id]: saved ? saved.name : "" };
    valuesRef.current = next;
    onValuesChange?.(next);
  };

  useEffect(() => {
    onReady?.(requiredFilled);
  }, [onReady, requiredFilled]);

  const handlePing = async () => {
    if (pinging) return;
    const check = validateMonitoringSignalsUrl(values?.monitors);
    if (!check.value) {
      setPing({ ok: false, message: "Enter a monitoring URL." });
      return;
    }
    if (!check.ok) {
      setPing({ ok: false, message: check.error || "Enter a valid monitoring URL" });
      return;
    }
    setPinging(true);
    setPing(null);
    try {
      const data = await pingMonitoringUrl(check.value);
      const reachable = data?.reachable === true;
      setPing({
        ok: reachable,
        message: String(
          data?.message || (reachable ? "Dashboard responded." : "The monitoring URL could not be reached.")
        ).slice(0, 200),
      });
    } catch (err) {
      const serverMessage = err?.response?.data?.message;
      const timedOut = err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT";
      setPing({
        ok: false,
        message: String(
          serverMessage
            || (timedOut
              ? "The monitoring URL did not respond in time."
              : "The monitoring URL could not be reached.")
        ).slice(0, 200),
      });
    } finally {
      setPinging(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.25, sm: 1.5, lg: 2 }, width: "100%", maxWidth: { xs: "100%", lg: 1012 } }}>
      {ITEMS.map((item) => (
        <ChecklistCard
          key={item.id}
          item={item}
          value={values[item.id]}
          onChange={(next) => {
            if (item.id === "monitors") setPing(null);
            onValuesChange?.({ ...values, [item.id]: next });
          }}
          onPing={item.id === "monitors" ? handlePing : undefined}
          pinging={item.id === "monitors" && pinging}
          pingResult={item.id === "monitors" ? ping : null}
          customDays={customDays}
          onCustomDays={onCustomDays}
          mdFile={shownFiles[item.id]}
          disabled={item.id === "trace" && traceDisabled}
          warning={item.id === "trace" && agenticTraceEnabled === false ? TRACE_WARNING : ""}
          onMdPick={item.kind === "md" ? async (file) => setMdResult(item.id, await inspectMdFile(file)) : undefined}
          onMdClear={item.kind === "md" ? () => setMdResult(item.id, { name: "", error: MD_REQUIRED_MSG }) : undefined}
        />
      ))}
    </Box>
  );
}
